import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Popconfirm,
  Tag,
  Space,
  MessagePlugin,
  Dialog,
  Form,
  Input,
} from 'tdesign-react';
import { DeleteIcon } from 'tdesign-icons-react';
import { eCardApi } from '../../api';
import type { eCard } from '../../types/banner';

export default function EcardManage() {
  const [data, setData] = useState<eCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const columns = [
    { colKey: 'name', title: '姓名', width: 100 },
    { colKey: 'phone', title: '电话', width: 130 },
    { colKey: 'position', title: '职业', width: 120 },
    { colKey: 'company', title: '公司', width: 150, ellipsis: true },
    { colKey: 'address', title: '公司地址', width: 200, ellipsis: true },
    { colKey: 'email', title: '邮箱', width: 180, ellipsis: true },
    {
      colKey: 'backgroundImage',
      title: '背景图',
      width: 120,
      cell: ({ row }: any) => (
        <div
          className="cursor-pointer"
          onClick={() => {
            setPreviewImage(row.backgroundImage);
            setPreviewVisible(true);
          }}
        >
          <img
            src={row.backgroundImage}
            alt="背景图"
            className="w-16 h-10 object-cover rounded hover:opacity-80 transition-opacity"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      ),
    },
    {
      colKey: 'fontColor',
      title: '字体颜色',
      width: 100,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <span
            className="w-6 h-6 rounded-full border border-gray-300"
            style={{ backgroundColor: row.fontColor }}
          ></span>
          <span className="text-sm text-gray-600">{row.fontColor}</span>
        </div>
      ),
    },
    {
      colKey: 'status',
      title: '状态',
      width: 80,
      cell: ({ row }: any) => (
        <Tag theme={row.status === 1 ? 'success' : 'default'}>
          {row.status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    { colKey: 'createTime', title: '创建时间', width: 180 },
    {
      colKey: 'action',
      title: '操作',
      width: 100,
      cell: ({ row }: any) => (
        <Space>
          <Popconfirm content="确定删除该电子名片？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchData = async (params: { pageNum?: number; pageSize?: number } = {}) => {
    setLoading(true);
    try {
      const res = await eCardApi.getPageList({
        pageNum: params.pageNum || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
      });
      if (res.code === 200) {
        setData(res.data.list || res.data.records || []);
        setPagination({
          ...pagination,
          total: res.data.total,
          ...(params.pageNum && { current: params.pageNum }),
          ...(params.pageSize && { pageSize: params.pageSize }),
        });
      }
    } catch {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id: number) => {
    try {
      const res = await eCardApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch {
      MessagePlugin.error('删除失败');
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold">电子名片管理</h1>
      </div>

      <Table
        columns={columns}
        data={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (pageInfo: { current: number; pageSize: number }) => {
            fetchData(pageInfo);
          },
        }}
        rowKey="id"
      />

      <Dialog
        visible={previewVisible}
        header="图片预览"
        onClose={() => setPreviewVisible(false)}
        footer={null}
        style={{ width: '600px' }}
      >
        <div className="flex justify-center items-center py-4">
          <img
            src={previewImage}
            alt="预览"
            className="max-w-full max-h-[500px] object-contain rounded-lg"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '';
            }}
          />
        </div>
      </Dialog>
    </div>
  );
}